import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";

import { BaseComponentContext } from "../../services/base-component-context.service";
import { BaseComponent } from "../base-component/base-component.component";
import { SearchBaseResponse } from "../../models/search/search-base-response";
import { SearchBaseRequest } from "../../models/search/search-base-request";
import { SearchPaging } from "../../models/search/search-paging";
import { SearchSorting } from "../../models/search/search-sorting";
import { Deferred } from "../../helpers/deferred";
import { Directive } from "@angular/core";

@Directive()
export abstract class BaseSearch<TRequest extends SearchBaseRequest, TResponse extends SearchBaseResponse<any>> extends BaseComponent<TRequest, TResponse> {
    
    public readonly searchInstance: BaseSearch<TRequest, TResponse>;

    private requestCountCancellationTokenSource: Deferred;

    private searchTermChangedSubject: Subject<string> = new Subject<string>();
    private searchTermChangedSubscription: Subscription;

    constructor(protected override componentContext: BaseComponentContext, protected override route: ActivatedRoute) {
            super(componentContext, route);
        this.replaceHistoryOnRequestModelChanged = false;
        this.searchInstance = this;

        this.searchTermChangedSubscription = this.searchTermChangedSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                tap(_ => this.onSearchTermChanged())
            ).subscribe();
    }

    get searchTerm(): string {
            return this.requestModel ? this.requestModel.searchTerm : null;
    }

    set searchTerm(value: string) {
            if (this.requestModel) {
                this.requestModel.searchTerm = value;

            this.searchTermChangedSubject.next(value ? value.trim() : null);
        }
    }

    override ngOnDestroy() {
            super.ngOnDestroy();

        this.searchTermChangedSubscription.unsubscribe();
    }

    public back() {
            this.goBack();
    }

    public onPagingChanged() {
            super.refresh();
    }

    public onSortChanged() {
            this.refresh();
    }

    public onFilterSubmitted() {
            this.requestModel.searchTerm = null;
        this.refresh();
    }

    public onFilterCleared() {
            (this.requestModel as any).filters = {} as any;
    }

    protected onSearchTermChanged() {
            if (this.requestModel.searchTerm) {
                (this.requestModel as any).filters = {} as any;
        }
        else {
                const model: any = this.createRequestModel();

            (this.requestModel as any).filters = model.filters;
        }

        this.refresh();
    }

    //TODO: remove this after advanced search will be refactored
    protected override refresh() {
            this.requestModel.paging.currentPage = 0;
        super.refresh();
    }

    public requestTotalAsync(): Promise<number> {
            if (this.requestCountCancellationTokenSource) {
                this.requestCountCancellationTokenSource.resolve();
        }

        const requestCountCancellationTokenSource = this.componentCancellationTokenSource.createDeferred();

        this.refreshViewModelCancellationToken.then(() => {
                requestCountCancellationTokenSource.resolve();
        });

        this.requestCountCancellationTokenSource = requestCountCancellationTokenSource;

        const json = JSON.stringify(this.requestModel, (key, value) => key != '____timestamp' && value != undefined && value !== '' ? value : undefined);
        const countRequest = JSON.parse(json) as TRequest;
        countRequest.requestTotal = true;

        const countRequestJson = JSON.stringify(countRequest);

        const resultPromise = new Promise<number>((resolve, reject) => {
                this.componentContext.http.getJsonAsync<TResponse>(this.actionName + "?request=" + encodeURIComponent(countRequestJson),
                null, requestCountCancellationTokenSource.promise()).then(val => {
                        this.viewModel.total = val.total;
                    resolve(0); // possible error on migration
                }, error => {
                        resolve(0) //  possible error on migration
                });
        });

        return resultPromise;
    }

    protected createRequestModel(): TRequest {
        
        const request = {} as TRequest;
        request.paging = new SearchPaging();
        request.sorting = new SearchSorting();

        (request as any).filters = {};

        return request as any;
    }
}